/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ConfigENV } from '@/init';
import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js';
import { AuthService } from './types';

const DEFAULT_MIN_TOKEN_VALIDITY = 30; // minutes;

export class OIDCServiceKeycloak extends AuthService {
  private keycloak: Keycloak;
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly minValidity: number;
  private initOptions: KeycloakInitOptions = {
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256'
  };
  private initPromise: Promise<boolean> | null = null;

  constructor(private config: NonNullable<ConfigENV['SERVER_CONFIG']['oidc']>) {
    super();
    this.minValidity = config.minTokenValidity ?? DEFAULT_MIN_TOKEN_VALIDITY;
    const keycloakConfig: KeycloakConfig = {
      url: this.config.issuer.endsWith('/') ? this.config.issuer.slice(0, -1) : this.config.issuer,
      realm: this.config.realm!,
      clientId: this.config.clientId
    };
    this.keycloak = new Keycloak(keycloakConfig);
  }

  /**
   * Initializes the Keycloak adapter exactly once per instance. With
   * `login-required` this forces authentication and, on a page reload, silently
   * restores the session (and a fresh access token) from the Keycloak SSO
   * cookie so the in-memory token is not lost. The init promise is cached (even
   * on failure) because a Keycloak instance can only be initialized once.
   */
  async initialize(): Promise<boolean> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.keycloak.init(this.initOptions);
    try {
      const authenticated = await this.initPromise;
      if (authenticated) {
        this.setupTokenRefresh();
      }
      return authenticated;
    } catch (error) {
      console.error('[Keycloak] Initialization failed:', error);
      this.initPromise = null;
      throw error;
    }
  }

  useWithCredentials(): boolean {
    return false;
  }

  getToken(): string | null {
    return this.keycloak.token ?? null;
  }

  toJSON(): string {
    return `OIDCServiceKeycloak(${this.keycloak.clientId})`;
  }

  isExpired(): boolean {
    if (!this.keycloak.token) {
      return true;
    }
    return this.keycloak.isTokenExpired(this.minValidity);
  }

  async login(redirect?: string): Promise<void> {
    await this.initialize();
    return this.keycloak.login({ redirectUri: redirect });
  }

  async logout(redirect?: string): Promise<void> {
    return this.keycloak.logout({ redirectUri: redirect });
  }

  async refreshToken(): Promise<void> {
    try {
      await this.keycloak.updateToken(this.minValidity);
    } catch (error) {
      console.error('[Keycloak] Token refresh failed:', error);
      await this.logout();
    }
  }

  private setupTokenRefresh(): void {
    const refresh = () => {
      this.keycloak.updateToken(this.minValidity).catch((error) => {
        console.error('[Keycloak] Token refresh failed:', error);
      });
    };

    this.keycloak.onTokenExpired = refresh;

    if (this.refreshIntervalId !== null) {
      clearInterval(this.refreshIntervalId);
    }
    this.refreshIntervalId = setInterval(refresh, this.minValidity * 60 * 1000);
  }
}
